import * as React from 'react'
import { useAuth } from './auth-context'
import {
  getWishlist as getWishlistApi,
  addToWishlist as addToWishlistApi,
  removeFromWishlist as removeFromWishlistApi,
} from '../services/wishlistApi'

const WISHLIST_STORAGE_KEY = 'guestWishlist'

// Guest Wishlist Storage Utilities
class GuestWishlistStorage {
  static get(): string[] {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]')
    } catch {
      return []
    }
  }

  static set(items: string[]): void {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items))
  }

  static add(productId: string): string[] {
    const items = this.get()
    if (!items.includes(productId)) {
      items.push(productId)
      this.set(items)
    }
    return items
  }

  static remove(productId: string): string[] {
    const items = this.get().filter((id) => id !== productId)
    this.set(items)
    return items
  }

  static clear(): void {
    localStorage.removeItem(WISHLIST_STORAGE_KEY)
  }
}

interface WishlistContextValue {
  wishlist: string[]
  loading: boolean
  addItem: (productId: string) => Promise<boolean>
  removeItem: (productId: string) => Promise<boolean>
  toggleItem: (productId: string) => Promise<boolean>
  isInWishlist: (productId: string) => boolean
  wishlistCount: number
  refreshWishlist: () => Promise<void>
}

export const WishlistContext = React.createContext<
  WishlistContextValue | undefined
>(undefined)

WishlistContext.displayName = 'WishlistContext'

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [wishlist, setWishlist] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(false)
  const isAuthenticated = !!user

  // Load wishlist on mount and when authentication status changes
  React.useEffect(() => {
    loadWishlist()
  }, [isAuthenticated])

  // Load wishlist (guest or authenticated)
  const loadWishlist = React.useCallback(async () => {
    if (isAuthenticated) {
      await loadUserWishlist()
    } else {
      loadGuestWishlist()
    }
  }, [isAuthenticated])

  // Load authenticated user's wishlist
  const loadUserWishlist = async () => {
    setLoading(true)
    try {
      const response = await getWishlistApi()

      if (response.success) {
        const productIds = response.data.items.map((item) => item.productId)
        setWishlist(productIds)
      }
    } catch (error) {
      console.error('Failed to load wishlist:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load guest wishlist from localStorage
  const loadGuestWishlist = () => {
    const guestWishlist = GuestWishlistStorage.get()
    setWishlist(guestWishlist)
  }

  // Refresh wishlist (can be called externally after merge)
  const refreshWishlist = React.useCallback(async () => {
    await loadWishlist()
  }, [loadWishlist])

  // Add item to wishlist
  const addItem = async (productId: string): Promise<boolean> => {
    if (isAuthenticated) {
      setLoading(true)
      try {
        const response = await addToWishlistApi(productId)

        if (response.success) {
          const productIds = response.data.items.map((item) => item.productId)
          setWishlist(productIds)
          return true
        }
        return false
      } catch (error) {
        console.error('Failed to add to wishlist:', error)
        return false
      } finally {
        setLoading(false)
      }
    } else {
      // Guest user - update localStorage
      const updated = GuestWishlistStorage.add(productId)
      setWishlist(updated)
      return true
    }
  }

  // Remove item from wishlist
  const removeItem = async (productId: string): Promise<boolean> => {
    if (isAuthenticated) {
      setLoading(true)
      try {
        const response = await removeFromWishlistApi(productId)

        if (response.success) {
          const productIds = response.data.items.map((item) => item.productId)
          setWishlist(productIds)
          return true
        }
        return false
      } catch (error) {
        console.error('Failed to remove from wishlist:', error)
        return false
      } finally {
        setLoading(false)
      }
    } else {
      // Guest user - update localStorage
      const updated = GuestWishlistStorage.remove(productId)
      setWishlist(updated)
      return true
    }
  }

  // Check if product is in wishlist
  const isInWishlist = (productId: string): boolean => {
    return wishlist.includes(productId)
  }

  // Toggle wishlist item
  const toggleItem = async (productId: string): Promise<boolean> => {
    if (isInWishlist(productId)) {
      return await removeItem(productId)
    } else {
      return await addItem(productId)
    }
  }

  const value = React.useMemo(
    () => ({
      wishlist,
      loading,
      addItem,
      removeItem,
      toggleItem,
      isInWishlist,
      wishlistCount: wishlist.length,
      refreshWishlist,
    }),
    [wishlist, loading, refreshWishlist],
  )

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  )
}

export function useWishlist() {
  const context = React.useContext(WishlistContext)
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
}

// Export storage utilities for use in auth context
export { GuestWishlistStorage }

